import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { RootState } from '../../../store';
import { ActiveTab, CreateMessageReplyType, SoundType } from '../../../utils/enums';
import type { GroupMember } from '../../GroupDetail/groupDetailSlice/types';
import type { SelectedGroup } from '../SelectGroupsList';
import type { CreateMessageState, SelectListUser, MessageContent, Sound } from './types';

const emptyMessageContent: MessageContent = {
  text: '',
  location: undefined,
  images: [],
  documents: [],
};

const initialState: CreateMessageState = {
  users: [],
  isLoading: false,
  areAdditionalUsersLoading: false,
  error: null,
  selectedUsers: [],
  selectedGroups: [],
  groupMembers: [],
  forwardedMessageID: null,
  selectedGroupType: [],
  activeTab: ActiveTab.Groups,
  usersLoading: false,
  subject: '',
  messageContent: emptyMessageContent,
  replyType: CreateMessageReplyType.replyAll,
  soundType: SoundType.Regular,
};

const createMessageSlice = createSlice({
  name: 'createMessage',
  initialState,
  reducers: {
    setGroupMembers: (state, action: PayloadAction<GroupMember[]>) => {
      state.groupMembers = action.payload;
    },
    setSelectedUsers: (state, action: PayloadAction<SelectListUser[]>) => {
      state.selectedUsers = action.payload;
    },
    setSelectedGroups: (state, action: PayloadAction<SelectedGroup[]>) => {
      state.selectedGroups = action.payload;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setUsersLoading: (state, action: PayloadAction<boolean>) => {
      state.usersLoading = action.payload;
    },
    setAreAdditionalUsersLoading: (state, action: PayloadAction<boolean>) => {
      state.areAdditionalUsersLoading = action.payload;
    },
    setIsError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    setActiveTab: (state, action: PayloadAction<ActiveTab>) => {
      state.activeTab = action.payload;
    },
    setUsers: (state, action: PayloadAction<SelectListUser[]>) => {
      state.users = action.payload;
    },
    deleteUser: (state, action: PayloadAction<number>) => {
      const selectedUsers = state.selectedUsers;
      state.selectedUsers = selectedUsers.filter(x => x.id !== action.payload);
    },
    setForwardedMessageID: (state, action: PayloadAction<number | null>) => {
      state.forwardedMessageID = action.payload;
    },
    setSelectedGroupType: (state, action: PayloadAction<number[]>) => {
      state.selectedGroupType = action.payload;
    },
    setSubject: (state, action: PayloadAction<string>) => {
      state.subject = action.payload;
    },
    setReplyType: (state, action: PayloadAction<CreateMessageReplyType>) => {
      state.replyType = action.payload;
    },
    setSoundType: (state, action: PayloadAction<Sound>) => {
      state.soundType = action.payload;
    },
    setMessageContentProperty: <K extends keyof MessageContent>(
      state: CreateMessageState,
      action: PayloadAction<{ key: K; value: MessageContent[K] }>
    ) => {
      const { key, value } = action.payload;
      state.messageContent[key] = value;
    },
    resetAll: state => {
      state.activeTab = ActiveTab.Groups;
      state.selectedUsers = [];
      state.groupMembers = [];
      state.selectedGroups = [];
      state.selectedGroupType = [];
      state.messageContent = emptyMessageContent;
      state.subject = '';
      state.replyType = CreateMessageReplyType.replyAll;
      state.soundType = SoundType.Regular;
    },
  },
});

export const {
  setGroupMembers,
  deleteUser,
  setSelectedUsers,
  setSelectedGroups,
  setIsLoading,
  setUsersLoading,
  setAreAdditionalUsersLoading,
  setIsError,
  setUsers,
  setActiveTab,
  setForwardedMessageID,
  setSelectedGroupType,
  setSubject,
  setMessageContentProperty,
  setReplyType,
  setSoundType,
  resetAll,
} = createMessageSlice.actions;

export const createMessageIsLoading = (state: RootState) => state.createMessage.isLoading;

export const createMessageUsersLoading = (state: RootState) => state.createMessage.usersLoading;

export const createMessageAdditionalUsersLoading = (state: RootState) =>
  state.createMessage.areAdditionalUsersLoading;

export const createMessageGetUsers = (state: RootState) => state.createMessage.users;

export const getSelectedUsers = (state: RootState) => state.createMessage.selectedUsers;

export const getSelectedGroups = (state: RootState) => state.createMessage.selectedGroups;

export const selectGroupMembers = (state: RootState) => state.createMessage.groupMembers;

export const getForwardedMessageID = (state: RootState) => state.createMessage.forwardedMessageID;

export const getSelectedGroupType = (state: RootState) => state.createMessage.selectedGroupType;

export const selectActiveTab = (state: RootState) => state.createMessage.activeTab;

export const selectSubject = (state: RootState) => state.createMessage.subject;

export const selectMessageContent = (state: RootState) => state.createMessage.messageContent;

export const selectReplyType = (state: RootState) => state.createMessage.replyType;

export const selectSoundType = (state: RootState) => state.createMessage.soundType;

export default createMessageSlice.reducer;
